/**
 * @file        src/components/BoardingBarcode.jsx
 * @description PDF417 boarding-pass barcode — the 2D stacked symbology real
 *              airline boarding passes carry — encoding an IATA BCBP (Bar Coded
 *              Boarding Pass, Resolution 792) M1 string: passenger name, PNR,
 *              origin, destination, carrier, flight number, Julian date of
 *              departure, cabin, seat and sequence number.
 *
 *              A VERBATIM port of the customer voucher's
 *              booking_frontends/<storefront>/src/pages/Voucher/BoardingBarcode.jsx
 *              so an admin reprint of an e-ticket carries the SAME scannable
 *              code as the copy the customer already holds. Keep the two in
 *              sync: a divergence here means one of the two tickets does not
 *              scan at the gate.
 * @author      Kinetika Labs
 * @created     2026-09-07
 * @changelog
 *   - 2026-09-07 Claude — Initial (ported for the admin e-ticket parity pass;
 *                          replaces the admin-only Code 39 barcode of the bare
 *                          PNR, which no airline gate can read).
 */
import { useEffect, useRef, useState } from 'react';
import bwipjs from 'bwip-js';

/** Day-of-year (1–366) — BCBP dates the flight by its Julian day, not a calendar date. */
function julianDay(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '000';
  const start = Date.UTC(d.getUTCFullYear(), 0, 0);
  const day = Math.floor((Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - start) / 86400000);
  return String(day).padStart(3, '0');
}

const pad = (v, n) => String(v ?? '').toUpperCase().slice(0, n).padEnd(n, ' ');

/**
 * IATA BCBP M1 string for one passenger on one sector.
 *
 * M1 | name(20) | E | ticket-indicator | PNR(7) | from(3) | to(3) | carrier(3) |
 * flight(5) | Julian(3) | cabin(1) | seat(4) | sequence(5) | pax-status(1)
 *
 * Every field comes from the booking. A seat that was never assigned encodes as
 * zeros — the gate reads that as "no seat", which is the truth; inventing one
 * would print a boarding pass for a seat the passenger does not hold.
 */
export function bcbpString({ passenger, pnr, from, to, carrier, flightNo, depISO, cabin, seat, seq = 1 }) {
  const last = String(passenger?.lastName || '').trim().toUpperCase();
  const first = String(passenger?.firstName || '').trim().toUpperCase();
  const name = pad(`${last}/${first}`, 20);
  const cabinCode = /BUS/i.test(cabin) ? 'C' : /FIRST/i.test(cabin) ? 'F' : /PREMIUM/i.test(cabin) ? 'W' : 'Y';
  const seatNo = String(seat || '').replace(/[^0-9A-Z]/gi, '').toUpperCase();

  // 60 characters, field-for-field as the IATA M1 mandatory section is laid out —
  // a scanner reads by OFFSET, so a field that is one character short shifts every
  // field after it and the pass will not parse at the gate.
  return [
    'M1',                                                    //  2  format + legs
    name,                                                    // 20  passenger name
    'E',                                                     //  1  e-ticket indicator
    pad(pnr, 7),                                             //  7  operating carrier PNR
    pad(from, 3),                                            //  3  origin
    pad(to, 3),                                              //  3  destination
    pad(carrier, 3),                                         //  3  operating carrier
    pad(String(flightNo || '').replace(/\D/g, '').padStart(4, '0'), 5), // 5 flight no.
    julianDay(depISO),                                       //  3  Julian date
    cabinCode,                                               //  1  compartment
    seatNo ? seatNo.padStart(4, '0') : '0000',               //  4  seat
    String(seq).padStart(5, '0'),                            //  5  check-in sequence
    '0',                                                     //  1  pax status: not checked in
    '00',                                                    //  2  conditional-field size
  ].join('');
}

export default function BoardingBarcode({ value, scale = 2, className = '' }) {
  const canvasRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canvasRef.current || !value) return;
    try {
      bwipjs.toCanvas(canvasRef.current, {
        bcid: 'pdf417',
        text: value,
        scale,
        includetext: false,
      });
      setFailed(false);
    } catch (e) {
      // A symbology failure must not take the e-ticket down with it — the ticket
      // is still valid without the scannable strip.
      console.error('PDF417 render failed:', e?.message || e);
      setFailed(true);
    }
  }, [value, scale]);

  if (failed) return null;
  return <canvas ref={canvasRef} className={className} aria-label="Boarding pass barcode" />;
}
